import React from 'react'
import { CheckCircleFilled, InfoCircleFilled } from '@ant-design/icons'
import {
  WhitelistDeviceInput,
  WhitelistDeviceStatus,
  DeviceRegistrationStatus,
} from '../../../../api'
import { Form, Input, Button, Descriptions, Alert, message } from 'admin-web-ui'
import { Store } from 'rc-field-form/lib/interface'
import { useAsyncFn } from 'react-use'
import {
  retrieveDeviceRegistrationStatus,
  DeviceNotFound,
} from '../graphqlHandlers/retrieve-device-registration-status'
import { useProjectContext } from '../../../../containers/Project'
import styled from 'styled-components'
import { whitelistDevice } from '../graphqlHandlers/whitelist-device'

const SearchButtonContainer = styled.div`
  margin: 10px 0;
  text-align: right;
`

const SearchButton = styled(Button)`
  width: 160px;
`

export const StyledAlert = styled(Alert)`
  margin-top: 10px;
`

const ResultSection = styled.div`
  margin-top: 15px;
  margin-bottom: 20px;
`

const StyledCheckCircleFilled = styled(CheckCircleFilled)`
  color: ${({ theme }) => theme.colors.darkMint};
`

const StyledInfoCircleFilled = styled(InfoCircleFilled)`
  color: ${({ theme }) => theme.colors.sunShade};
`

export const ClearHistoryButton = styled(Button)`
  margin-top: 16px;
`

interface FormData {
  deviceId: string
}

export const AndroidVendorIdLookupForm: React.FC = () => {
  const { projectClient } = useProjectContext()

  const [searchState, searchHandler] = useAsyncFn(async (values?: Store) => {
    if (!values) {
      return undefined
    }

    const formData = values as FormData

    return retrieveDeviceRegistrationStatus(projectClient, {
      deviceId: formData.deviceId,
      type: 'ANDROID',
    })
  })

  const [clearHistoryState, clearHistoryHandler] = useAsyncFn(async () => {
    if (!searchState.value || searchState.value === 'DeviceNotFound') {
      throw new Error('Unexpected search state.')
    }

    const input: WhitelistDeviceInput = {
      deviceId: searchState.value.deviceId,
      type: 'ANDROID',
    }

    const response = await whitelistDevice(projectClient, input)
    message.success('Device registration history cleared!')
    searchHandler(undefined)

    return response
  }, [searchState.value])

  return (
    <Form onFinish={searchHandler} layout="vertical">
      <Form.Item
        name="deviceId"
        label="Vendor ID"
        rules={[{ required: true, message: 'Please enter a Vendor ID' }]}
      >
        <Input placeholder="Paste in a Vendor ID" />
      </Form.Item>
      <SearchButtonContainer>
        <SearchButton
          type="primary"
          shape="round"
          htmlType="submit"
          loading={searchState.loading}
        >
          Search
        </SearchButton>
      </SearchButtonContainer>
      {searchState.error && (
        <StyledAlert
          className="Alert__search-result"
          type="error"
          message="An error occurred processing your request. Please try again."
        />
      )}
      {searchState.value && (
        <SearchResults
          searchStateValue={searchState.value}
          error={!!clearHistoryState.error}
          loading={clearHistoryState.loading}
          clearHistory={clearHistoryHandler}
        />
      )}
    </Form>
  )
}

interface SearchResultsProps {
  searchStateValue: DeviceRegistrationStatus | DeviceNotFound
  loading: boolean
  error: boolean
  clearHistory: () => Promise<WhitelistDeviceStatus>
}

export const SearchResults: React.FC<SearchResultsProps> = (props) => {
  const { loading, error, clearHistory, searchStateValue } = props

  return searchStateValue !== 'DeviceNotFound' ? (
    <ResultSection>
      <h3>Device History</h3>
      <Descriptions bordered column={1}>
        <Descriptions.Item label="Registration Status">
          {searchStateValue.hasReachedRegistrationLimit ? (
            <span>
              <StyledInfoCircleFilled /> Registration Limit Reached
            </span>
          ) : (
            <span>
              <StyledCheckCircleFilled /> Device limit has not been reached yet
            </span>
          )}
        </Descriptions.Item>
      </Descriptions>
      <ClearHistoryButton
        type="primary"
        shape="round"
        onClick={clearHistory}
        loading={loading}
        disabled={!searchStateValue.hasReachedRegistrationLimit}
      >
        Clear Device History
      </ClearHistoryButton>
      {error && (
        <StyledAlert
          className="Alert__clear-history"
          type="error"
          message="An error occurred while trying to clear the device history. Please try again."
        />
      )}
    </ResultSection>
  ) : (
    <StyledAlert
      type="info"
      message="There is no Android device associated with this Vendor ID."
      className="AndroidVendorIdLookupForm__device-found-info"
    />
  )
}
